/**
 * academy-ui/src/template/sections/canvas/CanvasOverlayReader.jsx
 *
 * READER-side renderer for a persisted PAGE-LEVEL Canvas Builder OVERLAY block. The
 * console persists, per page, a self-describing overlay payload:
 *
 *   page.canvasOverlay = {
 *     referenceWidth, theme: { textColor, backgroundColor, fontFamily },
 *     items: [ { id, type, geometry:{x,y,width,height,z}, content, style } ]
 *   }
 *
 * This component renders that block as an absolutely-positioned, full-cover layer on top
 * of the real published page so the free-form Text/Image/Grid/Button/Container/Paper
 * elements appear EXACTLY as they did in the builder preview:
 *
 *   - Geometry is scaled with the PURE reader primitives `computeScaleFactor` +
 *     `scaleLayout` from `../../layout/layoutCore`, measured against the layer's own
 *     width so at a viewport >= `referenceWidth` the scale factor is 1.0 and every box
 *     equals its persisted geometry exactly.
 *   - Theme is resolved through `canvasThemeResolver.resolveItemStyle`, replicating the
 *     console's `Item -> overlay theme -> Template_Theme` precedence.
 *   - The per-item look-and-feel (font size/weight/align, borders, radius, padding,
 *     shadow, opacity, object-fit, grid columns/gap, button variant) mirrors the
 *     console's `ItemView` so editor and reader agree on every styled detail.
 *
 * RESILIENCE: never throws. Tolerates missing/odd input — a non-object overlay, an
 * invalid reference width, an empty item list, or malformed items (skipped) all render
 * nothing harmful.
 *
 * ZERO-LEGACY / ISOLATION: imports ONLY React, the two pure canvas helpers in this
 * folder, and the pure `layoutCore` scaling math. Imports NOTHING from `admin-console-ui`.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { computeResponsiveScaleFactor, scaleLayout, validateElementLayout, validateReferenceWidth } from '../../layout/layoutCore';
import { resolveItemStyle } from './canvasThemeResolver';
import { iconPathByName } from './iconCatalog';
import { EngineImage } from 'canvas-engine/model/assetUrlResolver';

/** Maximum number of columns used to lay out an Image_Grid (mirrors the console). */
const MAX_GRID_COLS = 6;

/** Named box-shadow presets selectable per item (mirrors the console's ItemView). */
const SHADOWS = {
  none: 'none',
  sm: '0 1px 3px rgba(15,23,42,.16)',
  md: '0 6px 18px rgba(15,23,42,.18)',
  lg: '0 18px 50px rgba(15,23,42,.28)',
};

/** Finite number or fallback. */
function num(value, fallback) {
  return Number.isFinite(value) ? value : fallback;
}

/** True for a non-null, non-array plain object. */
function isPlainObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function assetUrlOf(value) {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (!isPlainObject(value)) return '';
  return String(value.displayLink || value.storedLink || value.url || value.src || '').trim();
}

function backgroundImageLayer(value) {
  const url = assetUrlOf(value).replace(/[{};]/g, '');
  if (!url) return undefined;
  if (/url\s*\(/i.test(url) || /gradient\s*\(/i.test(url)) return url;
  return `url("${url.replace(/"/g, '%22')}")`;
}

/** ceil(sqrt(n)) columns, capped at MAX_GRID_COLS (mirrors the console's ItemView). */
function gridColumns(cellCount) {
  const n = Number.isFinite(cellCount) && cellCount > 0 ? cellCount : 1;
  return Math.min(MAX_GRID_COLS, Math.max(1, Math.ceil(Math.sqrt(n))));
}

/**
 * Resolve a navigable href for an overlay item from its content.link (Link_Config) or
 * the legacy content.href. Internal paths get a leading slash; external values get an
 * https:// scheme when missing. Empty / disabled links resolve to '' (not a link).
 */
function overlayItemHref(content) {
  const c = isPlainObject(content) ? content : {};
  const link = isPlainObject(c.link) ? c.link : null;
  if (link && link.enabled === false) return '';
  let url = '';
  if (link && typeof link.url === 'string' && link.url.trim()) url = link.url.trim();
  if (!url && typeof c.href === 'string' && c.href.trim()) url = c.href.trim();
  if (!url || url === '#') return '';
  const external = (link && link.type === 'external') || /^(https?:|mailto:|tel:)/i.test(url) || /^www\./i.test(url);
  if (external) {
    if (/^(https?:|mailto:|tel:)/i.test(url) || url.startsWith('//')) return url;
    return `https://${url.replace(/^\/+/, '')}`;
  }
  if (!url.startsWith('/') && !url.startsWith('#')) return `/${url.replace(/^\/+/, '')}`;
  return url;
}

/** Whether the item's link opens in a new tab. */
function overlayItemNewTab(content) {
  const link = isPlainObject(content) && isPlainObject(content.link) ? content.link : null;
  return Boolean(link && (link.target === '_blank' || link.newTab === true));
}

/**
 * Scope an item's authored custom CSS to THAT item only, so it can never leak to other
 * elements. Selectors are prefixed with the item's `[data-canvas-overlay-item="id"]`
 * attribute; `&` (or `:root`) targets the item box itself, anything else targets a
 * descendant. Scripts / @-rules / dangerous tokens are dropped for safety.
 */
function scopeOverlayItemCss(css, id) {
  const raw = String(css || '')
    .replace(/<\/?style[^>]*>/gi, '')
    .replace(/javascript:|expression\s*\(|@import[^;]*;?/gi, '');
  const sel = `[data-canvas-overlay-item="${String(id == null ? '' : id).replace(/["\\]/g, '')}"]`;
  return raw.split('}').map((rule) => {
    const parts = rule.split('{');
    if (parts.length < 2) return '';
    const selector = parts[0].trim();
    const body = parts.slice(1).join('{').trim();
    if (!selector || !body || selector.startsWith('@')) return '';
    const scoped = selector.split(',').map((piece) => {
      const t = piece.trim();
      if (!t) return '';
      if (t === '&' || t === ':root' || t === ':host') return sel;
      if (t.startsWith('&')) return `${sel}${t.slice(1)}`;
      return `${sel} ${t}`;
    }).filter(Boolean).join(', ');
    return scoped ? `${scoped} { ${body} }` : '';
  }).filter(Boolean).join('\n');
}

/**
 * Read the available width of a container element, falling back to `window.innerWidth`.
 * Returns 0 when nothing can be measured (SSR / detached node).
 */
function readContainerWidth(el) {
  if (el) {
    if (typeof el.clientWidth === 'number' && el.clientWidth > 0) {
      return el.clientWidth;
    }
    if (typeof el.getBoundingClientRect === 'function') {
      const rect = el.getBoundingClientRect();
      if (rect && Number.isFinite(rect.width) && rect.width > 0) {
        return rect.width;
      }
    }
  }
  if (typeof window !== 'undefined' && Number.isFinite(window.innerWidth) && window.innerWidth > 0) {
    return window.innerWidth;
  }
  return 0;
}

/** Resolve the Template_Theme base from the published template (mirrors the reader). */
function templateThemeOf(template) {
  if (!template || typeof template !== 'object') return undefined;
  if (template.attributes && typeof template.attributes === 'object') {
    return template.attributes.theme;
  }
  return template.theme;
}

/**
 * Resolve a video URL into an embeddable form: a YouTube/Vimeo player iframe `src`, or a
 * direct media file. Returns `{ type: 'iframe'|'file'|'none', src }`.
 */
function videoEmbed(url) {
  const u = typeof url === 'string' ? url.trim() : '';
  if (!u) return { type: 'none', src: '' };
  // YouTube (watch?v=, youtu.be/, /embed/, /shorts/)
  const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (yt && yt[1]) return { type: 'iframe', src: `https://www.youtube.com/embed/${yt[1]}` };
  // Vimeo
  const vm = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm && vm[1]) return { type: 'iframe', src: `https://player.vimeo.com/video/${vm[1]}` };
  // Direct media file
  if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(u)) return { type: 'file', src: u };
  // Fallback: treat as an embeddable iframe URL if it's http(s)
  if (/^https?:\/\//i.test(u)) return { type: 'iframe', src: u };
  return { type: 'none', src: '' };
}

/** Minimal sanitizer for the Embed element: strips scripts and inline handlers while
 *  keeping safe markup (incl. iframes for legitimate embeds). */
function sanitizeEmbedHtml(html) {
  return String(html || '')
    .replace(/<\s*script[\s\S]*?<\s*\/\s*script\s*>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .replace(/\son\w+\s*=\s*[^\s>]+/gi, '')
    .replace(/javascript:/gi, '');
}

/** A visible "fill in this element" placeholder shown only while editing. */
function editPlaceholder(label, fontFamily) {
  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366f1', fontFamily, fontSize: 12, fontWeight: 700, textAlign: 'center', padding: 8 }}>
      {label}
    </div>
  );
}

/** Interactive accordion (published page); static (first item open) while editing. */
function AccordionView({ items, resolved, st, editing }) {
  const [openIdx, setOpenIdx] = React.useState(0);
  const list = Array.isArray(items) ? items : [];
  const color = resolved.textColor || '#1f2937';
  const fs = Number.isFinite(st.fontSize) ? st.fontSize : 15;
  return (
    <div style={{ width: '100%', height: '100%', overflow: 'auto', fontFamily: resolved.fontFamily, color }}>
      {list.map((it, i) => (
        <div key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
          <button
            type="button"
            onClick={editing ? undefined : () => setOpenIdx(openIdx === i ? -1 : i)}
            style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 0, padding: '10px 4px', fontWeight: 700, fontSize: fs, cursor: editing ? 'default' : 'pointer', color: 'inherit', pointerEvents: editing ? 'none' : 'auto', display: 'flex', justifyContent: 'space-between', gap: 8 }}
          >
            <span>{it && it.q}</span>
            <span style={{ color: st.accentColor || '#4f46e5' }}>{openIdx === i ? '\u2212' : '+'}</span>
          </button>
          {openIdx === i ? <div style={{ padding: '0 4px 10px', fontSize: fs - 1, opacity: 0.82 }}>{it && it.a}</div> : null}
        </div>
      ))}
    </div>
  );
}

/** Interactive tabs (published page); static (first tab) while editing. */
function TabsView({ tabs, resolved, st, editing }) {
  const [active, setActive] = React.useState(0);
  const list = Array.isArray(tabs) ? tabs : [];
  const color = resolved.textColor || '#1f2937';
  const accent = st.accentColor || '#4f46e5';
  const fs = Number.isFinite(st.fontSize) ? st.fontSize : 14;
  const cur = list[active] || list[0] || {};
  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', fontFamily: resolved.fontFamily, color }}>
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
        {list.map((t, i) => (
          <button
            key={i}
            type="button"
            onClick={editing ? undefined : () => setActive(i)}
            style={{ background: 'transparent', border: 0, borderBottom: active === i ? `2px solid ${accent}` : '2px solid transparent', padding: '8px 10px', fontWeight: 700, fontSize: fs, color: active === i ? accent : 'inherit', cursor: editing ? 'default' : 'pointer', pointerEvents: editing ? 'none' : 'auto' }}
          >
            {t && t.label}
          </button>
        ))}
      </div>
      <div style={{ padding: '10px 4px', fontSize: fs, overflow: 'auto', flex: 1, whiteSpace: 'pre-wrap' }}>{cur.content}</div>
    </div>
  );
}

/** Render the per-type inner content for one validated item (mirrors the console). */
function renderItemContent(item, resolved, st, radius, editing) {
  const content = isPlainObject(item.content) ? item.content : {};
  // A configured link makes text/image navigable on the PUBLISHED page. While EDITING
  // (builder), we must NOT navigate — the click selects/drags the item — so the anchor
  // is only rendered when not editing.
  const href = (item.type === 'text' || item.type === 'image' || item.type === 'icon') ? overlayItemHref(content) : '';
  const asLink = href && !editing;
  const newTab = overlayItemNewTab(content);
  const linkWrap = (node, extraStyle) => (
    <a
      href={href}
      {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      style={{ display: 'block', width: '100%', height: '100%', textDecoration: 'none', color: 'inherit', ...(extraStyle || {}) }}
    >
      {node}
    </a>
  );
  switch (item.type) {
    case 'text': {
      const textNode = (
        <div
          style={{
            width: '100%',
            height: '100%',
            color: resolved.textColor,
            fontFamily: resolved.fontFamily,
            fontSize: num(st.fontSize, 16),
            fontWeight: st.fontWeight || 400,
            textAlign: st.textAlign || 'left',
            lineHeight: num(st.lineHeight, 1.4),
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {typeof content.text === 'string' ? content.text : ''}
        </div>
      );
      return asLink ? linkWrap(textNode) : textNode;
    }
    case 'image': {
      // Empty src: on the PUBLISHED page render nothing; while EDITING show a visible
      // placeholder so the author can see the dropped image box and select it to upload.
      if (!content.src) {
        if (!editing) return null;
        return (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6366f1',
              fontFamily: resolved.fontFamily,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              borderRadius: radius,
            }}
          >
            Image — click to upload
          </div>
        );
      }
      const imgNode = (
        <EngineImage
          src={content.src}
          alt={content.alt || ''}
          style={{
            width: '100%',
            height: '100%',
            objectFit: st.objectFit || 'cover',
            display: 'block',
            borderRadius: radius,
          }}
        />
      );
      return asLink ? linkWrap(imgNode, { borderRadius: radius }) : imgNode;
    }
    case 'grid': {
      const cells = Array.isArray(content.cells) ? content.cells : [];
      const cols = num(
        st.gridColumns,
        gridColumns(Number.isFinite(content.cellCount) ? content.cellCount : cells.length),
      );
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gap: num(st.gridGap, 4),
            boxSizing: 'border-box',
          }}
        >
          {cells.map((cell, idx) =>
            cell && cell.src ? (
              <EngineImage
                key={idx}
                src={cell.src}
                alt={cell.alt || ''}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: st.objectFit || 'cover',
                  display: 'block',
                  borderRadius: radius,
                }}
              />
            ) : (
              <span key={idx} style={{ display: 'block', width: '100%', height: '100%' }} />
            ),
          )}
        </div>
      );
    }
    case 'button': {
      const label = typeof content.label === 'string' && content.label ? content.label : 'Button';
      const variant = st.buttonVariant || 'filled';
      const fill = resolved.backgroundColor || '#2563eb';
      const fg = resolved.textColor || (variant === 'filled' ? '#ffffff' : fill);
      const variantStyle =
        variant === 'outline'
          ? { background: 'transparent', color: fg, border: `1px solid ${fill}` }
          : variant === 'text'
            ? { background: 'transparent', color: fg, border: 'none' }
            : { background: fill, color: fg, border: 'none' };
      const sharedStyle = {
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        textDecoration: 'none',
        fontFamily: resolved.fontFamily,
        fontSize: num(st.fontSize, 14),
        fontWeight: st.fontWeight || 600,
        borderRadius: radius || undefined,
        padding: num(st.padding, 0) || '0 12px',
        ...variantStyle,
      };
      // A present href navigates; an empty href performs no navigation.
      return content.href ? (
        <a href={content.href} style={sharedStyle}>
          {label}
        </a>
      ) : (
        <button type="button" style={sharedStyle}>
          {label}
        </button>
      );
    }
    case 'icon': {
      // A recolorable vector icon from the catalog. Color comes from style.color (or the
      // resolved text color); `bold` thickens it via an outline stroke. A configured link
      // makes it navigable on the published page.
      const name = typeof content.icon === 'string' && content.icon ? content.icon : 'share';
      const path = iconPathByName(name);
      const color = st.color || resolved.textColor || '#2563eb';
      const bold = st.bold === true || Number(st.fontWeight) >= 600;
      const iconNode = (
        <svg
          viewBox="0 0 24 24"
          width="100%"
          height="100%"
          style={{ display: 'block' }}
          aria-hidden="true"
          focusable="false"
        >
          <path d={path} fill={color} stroke={bold ? color : 'none'} strokeWidth={bold ? 1.4 : 0} />
        </svg>
      );
      return asLink ? linkWrap(iconNode) : iconNode;
    }
    case 'divider': {
      const orientation = st.orientation === 'vertical' ? 'vertical' : 'horizontal';
      const color = st.dividerColor || resolved.textColor || '#cbd5e1';
      const thickness = num(st.dividerThickness, 2);
      const lineStyle = (st.dividerStyle === 'dashed' || st.dividerStyle === 'dotted') ? st.dividerStyle : 'solid';
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {orientation === 'vertical'
            ? <div style={{ height: '100%', borderLeft: `${thickness}px ${lineStyle} ${color}` }} />
            : <div style={{ width: '100%', borderTop: `${thickness}px ${lineStyle} ${color}` }} />}
        </div>
      );
    }
    case 'shape': {
      const shape = content.shape === 'circle' || content.shape === 'triangle' ? content.shape : 'rectangle';
      const fill = st.backgroundColor && st.backgroundColor !== 'transparent' ? st.backgroundColor : (resolved.backgroundColor || '#4f46e5');
      const bw = num(st.borderWidth, 0);
      const bc = st.borderColor || '#1e293b';
      const border = bw ? `${bw}px solid ${bc}` : undefined;
      const img = typeof content.image === 'string' ? content.image.trim() : '';
      const clip = shape === 'triangle' ? 'polygon(50% 0%, 0% 100%, 100% 100%)' : undefined;
      const shapeRadius = shape === 'circle' ? '50%' : (radius || undefined);
      // When an image is set, fill the shape with the (clipped) image; else a solid fill.
      if (img) {
        return (
          <EngineImage
            src={img}
            alt={content.alt || ''}
            style={{
              width: '100%',
              height: '100%',
              objectFit: st.objectFit || 'cover',
              display: 'block',
              borderRadius: shape === 'triangle' ? undefined : shapeRadius,
              clipPath: clip,
              border: shape === 'triangle' ? undefined : border,
              boxSizing: 'border-box',
            }}
          />
        );
      }
      return (
        <div style={{ width: '100%', height: '100%', background: fill, borderRadius: shape === 'triangle' ? undefined : shapeRadius, clipPath: clip, border: shape === 'triangle' ? undefined : border, boxSizing: 'border-box' }} />
      );
    }
    case 'social': {
      const links = Array.isArray(content.links) ? content.links : [];
      const color = st.iconColor || resolved.textColor || '#334155';
      const gap = num(st.iconGap, 12);
      const justify = st.textAlign === 'center' ? 'center' : st.textAlign === 'right' ? 'flex-end' : 'flex-start';
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: justify, gap }}>
          {links.map((lk, i) => {
            if (!isPlainObject(lk)) return null;
            const path = iconPathByName(lk.icon || 'link');
            const url = typeof lk.url === 'string' ? lk.url.trim() : '';
            const svgNode = (
              <svg viewBox="0 0 24 24" height="100%" style={{ width: 'auto', height: '100%', display: 'block' }} aria-hidden="true" focusable="false">
                <path d={path} fill={color} />
              </svg>
            );
            if (url && !editing) {
              const safe = /^(https?:|mailto:|tel:)/i.test(url) || url.startsWith('/') ? url : `https://${url.replace(/^\/+/, '')}`;
              return (
                <a key={i} href={safe} target="_blank" rel="noopener noreferrer" style={{ height: '100%', display: 'inline-flex' }}>
                  {svgNode}
                </a>
              );
            }
            return <span key={i} style={{ height: '100%', display: 'inline-flex' }}>{svgNode}</span>;
          })}
        </div>
      );
    }
    case 'quote': {
      const accent = st.accentColor || resolved.textColor || '#4f46e5';
      const textColor = resolved.textColor || '#1f2937';
      const content2 = content;
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', boxSizing: 'border-box' }}>
          <div style={{ width: 4, background: accent, borderRadius: 2, marginRight: 14, flex: '0 0 auto' }} />
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden' }}>
            <div style={{ color: textColor, fontFamily: resolved.fontFamily, fontSize: num(st.fontSize, 18), fontStyle: 'italic', lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {typeof content2.text === 'string' ? content2.text : ''}
            </div>
            {(content2.author || content2.role) ? (
              <div style={{ marginTop: 8, color: textColor, fontFamily: resolved.fontFamily, fontSize: num(st.authorSize, 13), fontWeight: 700 }}>
                {content2.author || ''}
                {content2.role ? <span style={{ fontWeight: 400, opacity: 0.7 }}>{` — ${content2.role}`}</span> : null}
              </div>
            ) : null}
          </div>
        </div>
      );
    }
    case 'video': {
      const url = typeof content.url === 'string' ? content.url.trim() : '';
      if (!url) {
        if (!editing) return null;
        return (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366f1', fontFamily: resolved.fontFamily, fontSize: 12, fontWeight: 700, textAlign: 'center', padding: 8 }}>
            Video — add a YouTube / Vimeo / MP4 link
          </div>
        );
      }
      const embed = videoEmbed(url);
      // While EDITING, suppress pointer events on the media so a click selects/drags the
      // item instead of interacting with the player; the published page is interactive.
      const mediaPointer = editing ? 'none' : 'auto';
      if (embed.type === 'iframe') {
        return (
          <iframe
            src={embed.src}
            title="Embedded video"
            style={{ width: '100%', height: '100%', border: 0, display: 'block', borderRadius: radius, pointerEvents: mediaPointer }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        );
      }
      if (embed.type === 'file') {
        return (
          <video src={embed.src} controls style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover', borderRadius: radius, pointerEvents: mediaPointer }} />
        );
      }
      return null;
    }
    case 'spacer':
      // Invisible spacer — occupies its box, renders nothing.
      return null;
    case 'card': {
      const surfaceBg = (st.backgroundColor && st.backgroundColor !== 'transparent') ? st.backgroundColor : (resolved.backgroundColor || '#ffffff');
      const titleColor = st.titleColor || resolved.textColor || '#111827';
      const contentColor = st.contentColor || resolved.textColor || '#111827';
      const titleFs = num(st.titleFontSize, num(st.fontSize, 16));
      const contentFs = num(st.contentFontSize, titleFs - 3);
      const titleFw = st.titleFontWeight || st.fontWeight || 800;
      const contentFw = st.contentFontWeight || '400';
      const titleFont = st.titleFontFamily || resolved.fontFamily;
      const contentFont = st.contentFontFamily || resolved.fontFamily;
      const align = st.textAlign || 'left';
      const btn = content.buttonLabel;
      return (
        <div style={{ width: '100%', height: '100%', background: surfaceBg, borderRadius: radius || 12, overflow: 'hidden', boxShadow: '0 6px 18px rgba(15,23,42,.10)', display: 'flex', flexDirection: 'column', fontFamily: resolved.fontFamily, textAlign: align }}>
          {content.image ? <EngineImage src={content.image} alt="" style={{ width: '100%', height: '52%', objectFit: 'cover', display: 'block' }} /> : null}
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minHeight: 0, textAlign: align }}>
            {content.title ? <div style={{ fontWeight: titleFw, fontSize: titleFs, color: titleColor, fontFamily: titleFont }}>{content.title}</div> : null}
            {content.text ? <div style={{ fontSize: contentFs, color: contentColor, opacity: 0.8, lineHeight: 1.4, overflow: 'hidden', fontWeight: contentFw, fontFamily: contentFont }}>{content.text}</div> : null}
            {btn ? (
              editing
                ? <span style={{ marginTop: 'auto', alignSelf: 'flex-start', background: st.accentColor || '#4f46e5', color: '#fff', padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 700 }}>{btn}</span>
                : <a href={content.buttonHref || '#'} style={{ marginTop: 'auto', alignSelf: 'flex-start', background: st.accentColor || '#4f46e5', color: '#fff', padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>{btn}</a>
            ) : null}
          </div>
        </div>
      );
    }
    case 'stat': {
      const color = resolved.textColor || '#0f172a';
      const accent = st.accentColor || '#4f46e5';
      const align = st.textAlign === 'left' ? 'flex-start' : st.textAlign === 'right' ? 'flex-end' : 'center';
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: align, justifyContent: 'center', fontFamily: resolved.fontFamily }}>
          <div style={{ fontWeight: 900, fontSize: num(st.fontSize, 40), color: accent, lineHeight: 1 }}>{content.value}</div>
          <div style={{ marginTop: 6, fontSize: num(st.labelSize, 14), color, opacity: 0.75 }}>{content.label}</div>
        </div>
      );
    }
    case 'badge': {
      const bg = (st.backgroundColor && st.backgroundColor !== 'transparent') ? st.backgroundColor : '#4f46e5';
      const fg = resolved.textColor || '#ffffff';
      const align = st.textAlign === 'left' ? 'flex-start' : st.textAlign === 'right' ? 'flex-end' : 'center';
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: align }}>
          <span style={{ background: bg, color: fg, borderRadius: 999, padding: '4px 12px', fontSize: num(st.fontSize, 12), fontWeight: 800, fontFamily: resolved.fontFamily, whiteSpace: 'nowrap' }}>{content.text}</span>
        </div>
      );
    }
    case 'list': {
      const listItems = Array.isArray(content.items) ? content.items : [];
      const variant = content.variant || 'check';
      const color = resolved.textColor || '#1f2937';
      const accent = st.accentColor || '#10b981';
      return (
        <ul style={{ width: '100%', height: '100%', overflow: 'auto', margin: 0, padding: 0, listStyle: 'none', fontFamily: resolved.fontFamily, color, fontSize: num(st.fontSize, 15) }}>
          {listItems.map((t, i) => (
            <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '4px 0' }}>
              {variant === 'check'
                ? <svg viewBox="0 0 24 24" width="18" height="18" style={{ flex: '0 0 auto', marginTop: 1 }}><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill={accent} /></svg>
                : variant === 'number'
                  ? <span style={{ flex: '0 0 auto', fontWeight: 700, color: accent }}>{`${i + 1}.`}</span>
                  : <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent, marginTop: 7, flex: '0 0 auto' }} />}
              <span>{t}</span>
            </li>
          ))}
        </ul>
      );
    }
    case 'progress': {
      const v = Math.max(0, Math.min(100, num(content.value, 0)));
      const barColor = st.barColor || st.accentColor || resolved.backgroundColor || '#4f46e5';
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
          <div style={{ width: '100%', height: num(st.barHeight, 10), background: '#e5e7eb', borderRadius: 999, overflow: 'hidden' }}>
            <div style={{ width: `${v}%`, height: '100%', background: barColor, borderRadius: 999 }} />
          </div>
        </div>
      );
    }
    case 'rating': {
      const v = Math.max(0, Math.min(5, num(content.value, 0)));
      const color = st.accentColor || '#f59e0b';
      const align = st.textAlign === 'left' ? 'flex-start' : st.textAlign === 'right' ? 'flex-end' : 'center';
      const filled = Math.round(v);
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: align, gap: 2 }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <svg key={i} viewBox="0 0 24 24" style={{ height: '70%', width: 'auto', maxHeight: '100%' }}>
              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" fill={i < filled ? color : '#e5e7eb'} />
            </svg>
          ))}
        </div>
      );
    }
    case 'accordion':
      return <AccordionView items={content.items} resolved={resolved} st={st} editing={editing} />;
    case 'tabs':
      return <TabsView tabs={content.tabs} resolved={resolved} st={st} editing={editing} />;
    case 'map': {
      const q = typeof content.query === 'string' ? content.query.trim() : '';
      if (!q) return editing ? editPlaceholder('Map — enter an address/place', resolved.fontFamily) : null;
      const src = `https://www.google.com/maps?q=${encodeURIComponent(q)}&output=embed`;
      return <iframe src={src} title="Map" loading="lazy" style={{ width: '100%', height: '100%', border: 0, borderRadius: radius, display: 'block', pointerEvents: editing ? 'none' : 'auto' }} />;
    }
    case 'embed': {
      const html = typeof content.html === 'string' ? content.html : '';
      if (!html.trim()) return editing ? editPlaceholder('Embed — paste HTML / iframe', resolved.fontFamily) : null;
      return <div style={{ width: '100%', height: '100%', overflow: 'auto', pointerEvents: editing ? 'none' : 'auto' }} dangerouslySetInnerHTML={{ __html: sanitizeEmbedHtml(html) }} />;
    }
    case 'audio': {
      const url = typeof content.url === 'string' ? content.url.trim() : '';
      if (!url) return editing ? editPlaceholder('Audio — add an MP3/audio link', resolved.fontFamily) : null;
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
          <audio src={url} controls style={{ width: '100%', pointerEvents: editing ? 'none' : 'auto' }} />
        </div>
      );
    }
    case 'container':
    case 'paper':
    default:
      // Styled layout boxes: the visual comes from the box style; no inner content.
      return null;
  }
}

function overlayParentIdOf(item) {
  const content = isPlainObject(item && item.content) ? item.content : {};
  return typeof content.parentItemId === 'string' ? content.parentItemId : '';
}

function overlayDescendantIds(items, rootId) {
  const descendants = new Set();
  if (!Array.isArray(items) || !rootId) return descendants;
  let changed = true;
  while (changed) {
    changed = false;
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (!isPlainObject(item) || !item.id || descendants.has(item.id)) continue;
      const parentId = overlayParentIdOf(item);
      if (parentId === rootId || descendants.has(parentId)) {
        descendants.add(item.id);
        changed = true;
      }
    }
  }
  return descendants;
}

function overlaySameAnchor(a, b) {
  const ac = isPlainObject(a && a.content) ? a.content : {};
  const bc = isPlainObject(b && b.content) ? b.content : {};
  const aa = typeof ac.anchorSectionId === 'string' ? ac.anchorSectionId : '';
  const ba = typeof bc.anchorSectionId === 'string' ? bc.anchorSectionId : '';
  return aa === ba;
}

function overlayInside(child, parent) {
  const cg = child && child.geometry;
  const pg = parent && parent.geometry;
  if (!isPlainObject(cg) || !isPlainObject(pg)) return false;
  const cx = Number.isFinite(cg.x) ? cg.x : 0;
  const cy = Number.isFinite(cg.y) ? cg.y : 0;
  const cw = Number.isFinite(cg.width) ? cg.width : 0;
  const ch = Number.isFinite(cg.height) ? cg.height : 0;
  const px = Number.isFinite(pg.x) ? pg.x : 0;
  const py = Number.isFinite(pg.y) ? pg.y : 0;
  const pw = Number.isFinite(pg.width) ? pg.width : 0;
  const ph = Number.isFinite(pg.height) ? pg.height : 0;
  return cx >= px && cy >= py && cx + cw <= px + pw && cy + ch <= py + ph;
}

function overlayNestedIds(items, rootId) {
  const nested = overlayDescendantIds(items, rootId);
  if (!Array.isArray(items) || !rootId) return nested;
  const root = items.find((item) => isPlainObject(item) && item.id === rootId);
  if (!root || (root.type !== 'container' && root.type !== 'paper')) return nested;
  let changed = true;
  while (changed) {
    changed = false;
    const parents = new Set([rootId, ...nested]);
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (!isPlainObject(item) || !item.id || item.id === rootId || nested.has(item.id)) continue;
      for (const parentId of parents) {
        const parent = items.find((candidate) => isPlainObject(candidate) && candidate.id === parentId);
        if (parent && (parent.type === 'container' || parent.type === 'paper') && overlaySameAnchor(item, parent) && overlayInside(item, parent)) {
          nested.add(item.id);
          changed = true;
          break;
        }
      }
    }
  }
  return nested;
}

function overlayIsContainerLike(item) {
  return isPlainObject(item) && (item.type === 'container' || item.type === 'paper');
}

function overlayArea(item) {
  const g = item && item.geometry;
  if (!isPlainObject(g)) return Infinity;
  const w = Number.isFinite(g.width) ? g.width : 0;
  const h = Number.isFinite(g.height) ? g.height : 0;
  return Math.max(0, w) * Math.max(0, h);
}

function overlayParentForItem(item, candidates) {
  if (!isPlainObject(item) || !Array.isArray(candidates)) return '';
  const explicit = overlayParentIdOf(item);
  if (explicit) {
    const parent = candidates.find((candidate) => candidate && candidate.id === explicit);
    if (overlayIsContainerLike(parent)) return explicit;
  }
  let best = null;
  for (let i = 0; i < candidates.length; i += 1) {
    const candidate = candidates[i];
    if (!overlayIsContainerLike(candidate) || candidate.id === item.id) continue;
    if (!overlaySameAnchor(item, candidate)) continue;
    if (!overlayInside(item, candidate)) continue;
    if (overlayArea(candidate) <= overlayArea(item)) continue;
    if (!best || overlayArea(candidate) <= overlayArea(best)) best = candidate;
  }
  return best && best.id ? best.id : '';
}

function normalizeOverlayItem(rawItem) {
  if (!isPlainObject(rawItem)) return null;
  const geometryResult = validateElementLayout(rawItem.geometry);
  if (!geometryResult.ok) return null;
  return {
    id: rawItem.id,
    type: rawItem.type,
    geometry: geometryResult.value,
    content: isPlainObject(rawItem.content) ? rawItem.content : {},
    style: isPlainObject(rawItem.style) ? rawItem.style : {},
  };
}

/**
 * One overlay item. In read-only mode it is a static positioned box (published page).
 * When `editing.enabled`, it becomes selectable + draggable: a pointer drag moves it
 * locally (smooth) and, on release, reports the new geometry (in REFERENCE units, i.e.
 * pixel delta / scaleFactor) back to the console via `editing.onMove`. A selected item
 * shows an outline and a ✕ delete affordance. This is the SAME DOM the published page
 * renders, so positions agree exactly.
 */
function OverlayItem({ item, box, resolved, st, radius, baseBoxStyle, editing, children }) {
  const enabled = Boolean(editing && editing.enabled);
  const selected = enabled && editing.selectedId === item.id;
  const [delta, setDelta] = useState(null); // { mode:'move'|'resize', dx, dy } live offset
  const stateRef = useRef(null);
  const sf = editing && editing.scaleFactor > 0 ? editing.scaleFactor : 1;

  const begin = (mode) => (e) => {
    if (!enabled) return;
    e.preventDefault();
    e.stopPropagation();
    if (typeof editing.onSelect === 'function') editing.onSelect(item.id);
    // Locked items stay selectable (so they can be unlocked) but never move/resize.
    if (st && st.locked) return;
    stateRef.current = { mode, startX: e.clientX, startY: e.clientY, moved: false };
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch (err) { /* ignore */ }
  };

  const onPointerMove = (e) => {
    if (!enabled || !stateRef.current) return;
    const dx = e.clientX - stateRef.current.startX;
    const dy = e.clientY - stateRef.current.startY;
    if (!stateRef.current.moved && Math.hypot(dx, dy) < 3) return;
    stateRef.current.moved = true;
    setDelta({ mode: stateRef.current.mode, dx, dy });
  };

  const onPointerUp = (e) => {
    if (!enabled || !stateRef.current) return;
    const dx = e.clientX - stateRef.current.startX;
    const dy = e.clientY - stateRef.current.startY;
    const { mode, moved } = stateRef.current;
    stateRef.current = null;
    setDelta(null);
    if (moved && typeof editing.onMove === 'function') {
      if (mode === 'resize') {
        const width = Math.max(16, Math.round(item.geometry.width + dx / sf));
        const height = Math.max(16, Math.round(item.geometry.height + dy / sf));
        editing.onMove(item.id, { ...item.geometry, width, height });
      } else {
        const snap = (v) => Math.round(v / 8) * 8;
        const nextX = Math.max(0, snap(item.geometry.x + dx / sf));
        const nextY = Math.max(0, snap(item.geometry.y + dy / sf));
        editing.onMove(item.id, { ...item.geometry, x: nextX, y: nextY });
      }
    }
  };

  const moving = delta && delta.mode === 'move';
  const resizing = delta && delta.mode === 'resize';
  const liveGeometry = {
    x: moving ? Math.max(0, Math.round(item.geometry.x + delta.dx / sf)) : item.geometry.x,
    y: moving ? Math.max(0, Math.round(item.geometry.y + delta.dy / sf)) : item.geometry.y,
    width: resizing ? Math.max(16, Math.round(item.geometry.width + delta.dx / sf)) : item.geometry.width,
    height: resizing ? Math.max(16, Math.round(item.geometry.height + delta.dy / sf)) : item.geometry.height,
  };
  const rot = num(st && st.rotation, 0) ? ` rotate(${num(st.rotation, 0)}deg)` : '';
  const locked = Boolean(st && st.locked);
  const style = {
    ...baseBoxStyle,
    transform: moving
      ? `translate(${box.x + delta.dx}px, ${box.y + delta.dy}px)${rot}`
      : baseBoxStyle.transform,
    width: resizing ? Math.max(16, box.width + delta.dx) : baseBoxStyle.width,
    height: resizing ? Math.max(16, box.height + delta.dy) : baseBoxStyle.height,
    cursor: enabled ? (locked ? 'default' : 'move') : baseBoxStyle.cursor,
    pointerEvents: enabled ? 'auto' : baseBoxStyle.pointerEvents,
    outline: selected ? '2px solid #2563eb' : baseBoxStyle.outline,
    outlineOffset: selected ? '1px' : undefined,
    // While selected, float ABOVE all overlapping siblings so an overlapping
    // container/paper/grid can never cover this item or its handles — you can always
    // drag and expand the selected item even when items overlap.
    zIndex: selected ? 2147482000 : baseBoxStyle.zIndex,
  };

  return (
    <div
      data-canvas-overlay-item={item.id}
      style={style}
      onPointerDown={begin('move')}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {st && st.customCss ? <style>{scopeOverlayItemCss(st.customCss, item.id)}</style> : null}
      {renderItemContent(item, resolved, st, radius, enabled)}
      {children}
      {selected ? (
        <>
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: -10000,
              top: 0,
              width: 20000,
              height: 1,
              borderTop: '1px solid rgba(37,99,235,.74)',
              pointerEvents: 'none',
              zIndex: 2147482003,
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: -10000,
              top: '50%',
              width: 20000,
              height: 1,
              borderTop: '1px dashed rgba(37,99,235,.5)',
              pointerEvents: 'none',
              zIndex: 2147482003,
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: -10000,
              bottom: 0,
              width: 20000,
              height: 1,
              borderTop: '1px solid rgba(37,99,235,.74)',
              pointerEvents: 'none',
              zIndex: 2147482003,
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: -10000,
              left: 0,
              width: 1,
              height: 20000,
              borderLeft: '1px solid rgba(37,99,235,.74)',
              pointerEvents: 'none',
              zIndex: 2147482003,
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: -10000,
              left: '50%',
              width: 1,
              height: 20000,
              borderLeft: '1px dashed rgba(37,99,235,.5)',
              pointerEvents: 'none',
              zIndex: 2147482003,
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: -10000,
              right: 0,
              width: 1,
              height: 20000,
              borderLeft: '1px solid rgba(37,99,235,.74)',
              pointerEvents: 'none',
              zIndex: 2147482003,
            }}
          />
        </>
      ) : null}
      {selected ? (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 0,
            top: -32,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '5px 7px',
            borderRadius: 6,
            background: 'rgba(15,23,42,.94)',
            color: '#fff',
            font: '800 11px/1.2 Inter,Arial,sans-serif',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            boxShadow: '0 8px 18px rgba(15,23,42,.22)',
            zIndex: 2147482003,
          }}
        >
          x {Math.round(liveGeometry.x)} y {Math.round(liveGeometry.y)} | {Math.round(liveGeometry.width)} x {Math.round(liveGeometry.height)}
        </div>
      ) : null}
      {selected ? (
        <>
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: 0,
              top: -18,
              width: '100%',
              height: 8,
              pointerEvents: 'none',
              backgroundImage: 'linear-gradient(to right, rgba(37,99,235,.8) 1px, transparent 1px)',
              backgroundSize: '20px 8px',
              borderTop: '1px solid rgba(37,99,235,.8)',
              zIndex: 2147482003,
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: -18,
              top: 0,
              width: 8,
              height: '100%',
              pointerEvents: 'none',
              backgroundImage: 'linear-gradient(to bottom, rgba(37,99,235,.8) 1px, transparent 1px)',
              backgroundSize: '8px 20px',
              borderLeft: '1px solid rgba(37,99,235,.8)',
              zIndex: 2147482003,
            }}
          />
        </>
      ) : null}
      {selected ? (
        <div
          title="Drag to resize"
          onPointerDown={begin('resize')}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          style={{
            position: 'absolute',
            right: -8,
            bottom: -8,
            width: 18,
            height: 18,
            borderRadius: 5,
            background: '#2563eb',
            border: '2px solid #fff',
            boxShadow: '0 2px 6px rgba(15,23,42,.35)',
            cursor: 'nwse-resize',
            pointerEvents: 'auto',
            zIndex: 2147482002,
            display: locked ? 'none' : 'block',
          }}
        />
      ) : null}
      {selected ? (
        <button
          type="button"
          title="Delete item"
          onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (typeof editing.onDelete === 'function') editing.onDelete(item.id);
          }}
          style={{
            position: 'absolute',
            top: -12,
            right: -12,
            width: 24,
            height: 24,
            borderRadius: '50%',
            border: '2px solid #fff',
            background: '#dc2626',
            color: '#fff',
            fontSize: 13,
            fontWeight: 900,
            lineHeight: 1,
            cursor: 'pointer',
            boxShadow: '0 4px 10px rgba(15,23,42,.3)',
            pointerEvents: 'auto',
            zIndex: 2147482003,
          }}
        >
          &#x2715;
        </button>
      ) : null}
    </div>
  );
}

/**
 * Render the persisted page-level overlay directly from the payload.
 *
 * @param {object} props
 * @param {object} props.overlay - `page.canvasOverlay` ({ referenceWidth, theme, items }).
 * @param {object} [props.template] - The published template; its theme is the cascade base.
 * @param {object} [props.editing] - When `{ enabled }`, items become editable in place:
 *   `{ enabled, selectedId, scaleFactor, onSelect(id|null), onMove(id, geometry), onDelete(id) }`.
 *   Omitted/disabled on the published page (read-only).
 * @returns {JSX.Element|null} the overlay layer, or `null` when there is nothing to render.
 */
export default function CanvasOverlayReader({ overlay, template, editing }) {
  const layerRef = useRef(null);
  const [measuredWidth, setMeasuredWidth] = useState(0);
  // Pixel top of each real template section (keyed by data-reader-section-id), measured
  // relative to this overlay layer. Items anchored to a section (content.anchorSectionId)
  // are positioned relative to THIS offset, so they travel with the section when the
  // author reorders/swaps sections instead of staying at a fixed page coordinate.
  const [sectionOffsets, setSectionOffsets] = useState({});

  const hasOverlay = isPlainObject(overlay);
  const rw = hasOverlay ? validateReferenceWidth(overlay.referenceWidth) : { ok: false };
  // 1180 matches the editor stage / overlay document referenceWidth, so a point clicked
  // during placement maps to the same spot when the item is later rendered.
  const referenceWidth = rw.ok ? rw.value : 1180;

  // Measure the layer's own width for responsive scaling (SSR-safe).
  useLayoutEffect(() => {
    const el = layerRef.current;
    setMeasuredWidth(readContainerWidth(el));
    return undefined;
  }, []);

  useEffect(() => {
    const el = layerRef.current;
    if (!el) return undefined;

    const update = () => setMeasuredWidth(readContainerWidth(el));

    let resizeObserver = null;
    let onWindowResize = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(el);
    } else if (typeof window !== 'undefined') {
      onWindowResize = update;
      window.addEventListener('resize', onWindowResize);
    }
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      if (onWindowResize && typeof window !== 'undefined') {
        window.removeEventListener('resize', onWindowResize);
      }
    };
  }, []);

  // Measure each real section's pixel top relative to the overlay layer. Re-measured on
  // mount, on resize, and whenever the overlay/template change (e.g. after a section
  // reorder/swap re-renders the page), so anchored items follow their section.
  useEffect(() => {
    const el = layerRef.current;
    if (!el) return undefined;
    const sameMap = (a, b) => {
      const ak = Object.keys(a); const bk = Object.keys(b);
      if (ak.length !== bk.length) return false;
      return ak.every((k) => b[k] && Math.abs((a[k].top || 0) - (b[k].top || 0)) < 0.5 && Math.abs((a[k].height || 0) - (b[k].height || 0)) < 0.5);
    };
    const measure = () => {
      const layer = layerRef.current;
      if (!layer) return;
      const doc = layer.ownerDocument || document;
      const layerTop = layer.getBoundingClientRect().top;
      const next = {};
      doc.querySelectorAll('[data-reader-section-id]').forEach((node) => {
        const id = node.getAttribute('data-reader-section-id');
        if (!id) return;
        const r = node.getBoundingClientRect();
        next[id] = { top: r.top - layerTop, height: r.height };
      });
      setSectionOffsets((prev) => (sameMap(prev, next) ? prev : next));
    };
    // Measure after paint, then once more shortly after (sections/images can reflow).
    let raf = typeof window !== 'undefined' ? window.requestAnimationFrame(measure) : 0;
    const t = setTimeout(measure, 250);
    let ro = null;
    let onResize = null;
    const doc = el.ownerDocument || document;
    if (typeof ResizeObserver !== 'undefined' && doc.body) {
      ro = new ResizeObserver(measure);
      ro.observe(doc.body);
    } else if (typeof window !== 'undefined') {
      onResize = measure;
      window.addEventListener('resize', onResize);
    }
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      clearTimeout(t);
      if (ro) ro.disconnect();
      if (onResize && typeof window !== 'undefined') window.removeEventListener('resize', onResize);
    };
  }, [overlay, template, measuredWidth]);

  // DETERMINISTIC PLACEMENT CAPTURE.
  // Installed whenever the editor is enabled (NOT only while `placement` is true, which
  // lagged behind the user's click). A window capture-phase pointerdown reaches `window`
  // BEFORE the element editing layer's document-capture listeners, so when the SYNCHRONOUS
  // global flag `window.__AVPS_PLACING__` is set, we suppress selection and DROP here —
  // regardless of React state/mount timing. When not placing, the handler is inert.
  useEffect(() => {
    const enabledNow = Boolean(editing && editing.enabled);
    if (!enabledNow) return undefined;
    const isPlacing = () => (typeof window !== 'undefined' && window.__AVPS_PLACING__ === true);
    const doc = layerRef.current?.ownerDocument || (typeof document !== 'undefined' ? document : null);
    if (!doc) return undefined;
    const eventInsideLayer = (e) => {
      const el = layerRef.current;
      if (!el || typeof el.getBoundingClientRect !== 'function') return false;
      const rect = el.getBoundingClientRect();
      return e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
    };
    const dropAt = (e) => {
      const el = layerRef.current;
      if (!el || typeof editing.onPlace !== 'function') return;
      const rect = el.getBoundingClientRect();
      const width = readContainerWidth(el);
      const sf = computeResponsiveScaleFactor(width || referenceWidth, referenceWidth) || 1;
      const sfSafe = sf > 0 ? sf : 1;
      const x = Math.max(0, Math.round((e.clientX - rect.left) / sfSafe));
      const y = Math.max(0, Math.round((e.clientY - rect.top) / sfSafe));
      // Find the real section under the drop point so the item is ANCHORED to it (and
      // its y is stored relative to that section's top). elementsFromPoint returns the
      // overlay layer first (skip it) then the section beneath.
      const doc = el.ownerDocument || document;
      let sectionId = null;
      let relY = y;
      const stack = typeof doc.elementsFromPoint === 'function' ? doc.elementsFromPoint(e.clientX, e.clientY) : [];
      for (let i = 0; i < stack.length; i += 1) {
        const node = stack[i];
        if (!node || node === el) continue;
        const s = node.closest && node.closest('[data-reader-section-id]');
        if (s) {
          sectionId = s.getAttribute('data-reader-section-id');
          const sTopPx = s.getBoundingClientRect().top - rect.top;
          relY = Math.max(0, Math.round(y - sTopPx / sfSafe));
          break;
        }
      }
      if (typeof window !== 'undefined') window.__AVPS_PLACING__ = false;
      editing.onPlace(x, relY, sectionId);
    };
    const onPointerDownCapture = (e) => {
      if (!isPlacing() || typeof editing.onPlace !== 'function') return;
      if (e.button != null && e.button !== 0) return;
      if (!eventInsideLayer(e)) return;
      // Block the editing layer's selecting pointerdown and DROP here directly. NB: we
      // must NOT depend on the follow-up `click` event — preventDefault on pointerdown
      // suppresses that click in some browsers, which previously left placement "stuck".
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      dropAt(e);
    };
    const swallowClick = (e) => {
      if (!isPlacing()) return;
      if (!eventInsideLayer(e)) return;
      // Swallow the trailing click so it can't reach a re-enabled editing layer.
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    };
    doc.addEventListener('pointerdown', onPointerDownCapture, true);
    doc.addEventListener('click', swallowClick, true);
    return () => {
      doc.removeEventListener('pointerdown', onPointerDownCapture, true);
      doc.removeEventListener('click', swallowClick, true);
    };
  }, [editing, referenceWidth]);

  // BACKFILL anchors for LEGACY items (placed before items were section-bound, i.e. with
  // no content.anchorSectionId). Using the measured section ranges, infer the section
  // each such item currently sits in and report it to the Console so the binding
  // PERSISTS — afterwards the item travels with its section on reorder/publish. Runs only
  // while editing in the builder; reported once per item.
  const reportedAnchorRef = useRef(new Set());
  useEffect(() => {
    // Never backfill WHILE placing a new item — re-posting the doc mid-placement would
    // disrupt the drop and let a section steal focus. Only backfill in plain edit mode.
    if (!editing || !editing.enabled || editing.placement || typeof editing.onAnchor !== 'function') return;
    const list = isPlainObject(overlay) && Array.isArray(overlay.items) ? overlay.items : [];
    const keys = Object.keys(sectionOffsets);
    if (!list.length || !keys.length) return;
    const ew = (typeof measuredWidth === 'number' && measuredWidth > 0) ? measuredWidth : referenceWidth;
    const sf = computeResponsiveScaleFactor(ew, referenceWidth) || 1;
    const sfSafe = sf > 0 ? sf : 1;
    list.forEach((raw) => {
      if (!isPlainObject(raw) || raw.id == null) return;
      const hasAnchor = isPlainObject(raw.content) && typeof raw.content.anchorSectionId === 'string' && raw.content.anchorSectionId;
      if (hasAnchor || reportedAnchorRef.current.has(raw.id)) return;
      const g = validateElementLayout(raw.geometry);
      if (!g.ok) return;
      const absYpx = g.value.y * sfSafe;
      let foundId = '';
      let foundTop = 0;
      keys.forEach((sid) => {
        const s = sectionOffsets[sid];
        if (s && absYpx >= s.top - 1 && absYpx < s.top + s.height) { foundId = sid; foundTop = s.top; }
      });
      if (foundId) {
        reportedAnchorRef.current.add(raw.id);
        const relY = Math.max(0, Math.round(g.value.y - foundTop / sfSafe));
        editing.onAnchor(raw.id, foundId, relY);
      }
    });
  }, [overlay, editing, sectionOffsets, measuredWidth, referenceWidth]);

  // Keyboard nudge: move the selected overlay item with arrow keys (Shift = 10px) while
  // editing. Ignores key events from inputs/textareas so typing is unaffected.
  useEffect(() => {
    if (!editing || !editing.enabled || editing.placement) return undefined;
    const selId = editing.selectedId;
    if (!selId || typeof editing.onMove !== 'function') return undefined;
    const onKey = (e) => {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
      const tgt = e.target || {};
      const tag = tgt.tagName || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tgt.isContentEditable === true) return;
      const list = isPlainObject(overlay) && Array.isArray(overlay.items) ? overlay.items : [];
      const it = list.find((x) => isPlainObject(x) && x.id === selId);
      if (!it || !isPlainObject(it.geometry)) return;
      e.preventDefault();
      const step = e.shiftKey ? 10 : 1;
      const g = it.geometry;
      let x = Number.isFinite(g.x) ? g.x : 0;
      let y = Number.isFinite(g.y) ? g.y : 0;
      if (e.key === 'ArrowLeft') x = Math.max(0, x - step);
      else if (e.key === 'ArrowRight') x += step;
      else if (e.key === 'ArrowUp') y = Math.max(0, y - step);
      else if (e.key === 'ArrowDown') y += step;
      editing.onMove(selId, { ...g, x, y });
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [editing, overlay]);

  // Nothing to render only when NOT editing and there is nothing to show. While the
  // author is editing or placing, the full-page layer is always present so items can be
  // dropped/edited ANYWHERE across ALL sections (not just where items already exist).
  const editEnabledTop = Boolean(editing && editing.enabled);
  const placingTop = Boolean(editing && editing.placement);
  const editingActive = editEnabledTop || placingTop;
  const items = hasOverlay && Array.isArray(overlay.items) ? overlay.items : [];
  const selectedDescendants = editEnabledTop
    ? overlayNestedIds(items, editing && editing.selectedId)
    : new Set();
  if (!editingActive && (!hasOverlay || !rw.ok || items.length === 0)) return null;

  const overlayTheme = hasOverlay && isPlainObject(overlay.theme) ? overlay.theme : {};
  const templateTheme = templateThemeOf(template);
  // The resolver reads its Section_Theme overrides from the `theme` key of this model.
  const sectionLike = { theme: overlayTheme };

  // Effective width: measured layer width -> referenceWidth (-> scaleFactor 1.0 verbatim).
  const effectiveWidth =
    typeof measuredWidth === 'number' && Number.isFinite(measuredWidth) && measuredWidth > 0
      ? measuredWidth
      : referenceWidth;
  const scaleFactor = computeResponsiveScaleFactor(effectiveWidth, referenceWidth);

  const editEnabled = editEnabledTop;
  const placing = placingTop;
  // Editing carries the live scaleFactor so item drags convert pixels -> reference units.
  // While PLACING a new item, item editing is suppressed so a click never grabs/selects
  // an existing overlay item — every click goes to the layer and drops the new item.
  const itemEditing = editEnabled && !placing ? { ...editing, scaleFactor } : undefined;
  const normalizedItems = items.map(normalizeOverlayItem).filter(Boolean);
  const parentById = new Map();
  const childrenByParent = new Map();
  normalizedItems.forEach((item) => {
    const parentId = overlayParentForItem(item, normalizedItems);
    if (parentId) {
      parentById.set(item.id, parentId);
      if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
      childrenByParent.get(parentId).push(item);
    }
  });
  const topLevelItems = normalizedItems.filter((item) => !parentById.has(item.id));

  const renderOverlayItem = (item, index, parentItem) => {
    // Hidden items: render NOTHING on the published page. While editing they stay
    // visible (dimmed) so the author can select and un-hide them.
    if (item.style.hidden && !itemEditing) return null;

    // Top-level items anchored to a real template section are positioned relative to
    // that section. Nested items inherit the parent container's rendered position and
    // use coordinates relative to the parent, so they stay where they were dropped.
    const anchorId = typeof item.content.anchorSectionId === 'string' ? item.content.anchorSectionId : '';
    const sfForAnchor = scaleFactor > 0 ? scaleFactor : 1;
    const savedParentFrame = parentItem && isPlainObject(item.content && item.content.parentFrame)
      ? item.content.parentFrame
      : null;
    const savedParentFrameCurrent = parentItem && savedParentFrame && isPlainObject(parentItem.geometry)
      ? (
        Math.abs(num(savedParentFrame.x, 0) - num(parentItem.geometry.x, 0)) <= 1 &&
        Math.abs(num(savedParentFrame.y, 0) - num(parentItem.geometry.y, 0)) <= 1 &&
        Math.abs(num(savedParentFrame.width, 0) - num(parentItem.geometry.width, 0)) <= 1 &&
        Math.abs(num(savedParentFrame.height, 0) - num(parentItem.geometry.height, 0)) <= 1
      )
      : false;
    const savedRelative = parentItem &&
      savedParentFrameCurrent &&
      isPlainObject(item.content && item.content.relativeFrame)
      ? item.content.relativeFrame
      : null;
    const relativeGeometry = parentItem && savedRelative
      ? {
        ...item.geometry,
        x: Number.isFinite(savedRelative.x) ? savedRelative.x : item.geometry.x - parentItem.geometry.x,
        y: Number.isFinite(savedRelative.y) ? savedRelative.y : item.geometry.y - parentItem.geometry.y,
        width: Number.isFinite(savedRelative.width) ? savedRelative.width : item.geometry.width,
        height: Number.isFinite(savedRelative.height) ? savedRelative.height : item.geometry.height,
      }
      : parentItem && parentItem.geometry
        ? {
          ...item.geometry,
          x: item.geometry.x - parentItem.geometry.x,
          y: item.geometry.y - parentItem.geometry.y,
        }
        : item.geometry;
    const renderGeometry = !parentItem && anchorId && sectionOffsets[anchorId]
      ? { ...relativeGeometry, y: relativeGeometry.y + sectionOffsets[anchorId].top / sfForAnchor }
      : relativeGeometry;

    const box = scaleLayout(renderGeometry, scaleFactor);
    const resolved = resolveItemStyle(item, sectionLike, templateTheme);
    const st = item.style;

    const isPaper = item.type === 'paper';
    const isContainer = item.type === 'container';
    const radius = num(st.borderRadius, isPaper ? 12 : item.type === 'button' ? 8 : 0);
    const borderWidth = num(st.borderWidth, isContainer ? 1 : 0);
    const borderColor = st.borderColor || (isContainer ? '#cbd5e1' : '#e5e7eb');
    const padding = num(st.padding, isPaper || isContainer ? 16 : 0);
    const shadow = SHADOWS[st.boxShadow] || (isPaper ? SHADOWS.md : 'none');
    const opacity = num(st.opacity, 1);

    const backgroundColor = isPaper
      ? resolved.backgroundColor || '#ffffff'
      : isContainer
        ? resolved.backgroundColor || 'transparent'
        : item.type === 'text'
          ? resolved.backgroundColor
          : item.type === 'image'
            ? (item.content.src ? undefined : '#ffffff')
            : item.type === 'grid' || item.type === 'icon' || item.type === 'divider' || item.type === 'shape' || item.type === 'social' || item.type === 'quote' || item.type === 'video' || item.type === 'spacer' || item.type === 'card' || item.type === 'stat' || item.type === 'badge' || item.type === 'list' || item.type === 'progress' || item.type === 'rating' || item.type === 'accordion' || item.type === 'tabs' || item.type === 'map' || item.type === 'embed' || item.type === 'audio'
              ? undefined
              : resolved.backgroundColor;

    const baseBoxStyle = {
      position: 'absolute',
      top: 0,
      left: 0,
      transform: `translate(${box.x}px, ${box.y}px)${num(st.rotation, 0) ? ` rotate(${num(st.rotation, 0)}deg)` : ''}`,
      width: box.width,
      height: box.height,
      zIndex: box.z,
      boxSizing: 'border-box',
      backgroundColor,
      backgroundImage: backgroundImageLayer(st.backgroundImage),
      backgroundSize: backgroundImageLayer(st.backgroundImage) ? 'cover' : undefined,
      backgroundPosition: backgroundImageLayer(st.backgroundImage) ? 'center' : undefined,
      backgroundRepeat: backgroundImageLayer(st.backgroundImage) ? 'no-repeat' : undefined,
      opacity,
      borderRadius: radius || undefined,
      border: (borderWidth && item.type !== 'shape') ? `${borderWidth}px solid ${borderColor}` : undefined,
      boxShadow: shadow !== 'none' ? shadow : undefined,
      padding: (isPaper || isContainer) && padding ? padding : undefined,
      pointerEvents: placing ? 'none' : 'auto',
    };

    if (itemEditing) {
      baseBoxStyle.zIndex = 1000000 + (Number.isFinite(box.z) ? box.z : 0);
      if (parentItem || selectedDescendants.has(item.id)) {
        baseBoxStyle.zIndex = 2147482001 + (Number.isFinite(box.z) ? box.z : 0);
      }
      const emptyImage = item.type === 'image' && !item.content.src;
      const emptyGrid = item.type === 'grid'
        && !(Array.isArray(item.content.cells) && item.content.cells.some((c) => c && c.src));
      const noFill = !backgroundColor || backgroundColor === 'transparent';
      const emptyBox = (item.type === 'container' || item.type === 'paper') && noFill && !borderWidth;
      if (emptyImage || emptyGrid || emptyBox) {
        baseBoxStyle.outline = '2px dashed #6366f1';
        baseBoxStyle.outlineOffset = '-2px';
        baseBoxStyle.backgroundColor = backgroundColor && backgroundColor !== 'transparent'
          ? backgroundColor
          : 'rgba(99,102,241,0.10)';
      }
      if (item.style.hidden) {
        baseBoxStyle.opacity = 0.4;
        baseBoxStyle.outline = '2px dashed #94a3b8';
        baseBoxStyle.outlineOffset = '-2px';
      }
    }

    const childItems = childrenByParent.get(item.id) || [];
    return (
      <OverlayItem
        key={item.id != null ? item.id : index}
        item={item}
        box={box}
        resolved={resolved}
        st={st}
        radius={radius}
        baseBoxStyle={baseBoxStyle}
        editing={itemEditing}
      >
        {childItems.map((child, childIndex) => renderOverlayItem(child, childIndex, item))}
      </OverlayItem>
    );
  };

  return (
    <div
      ref={layerRef}
      data-canvas-overlay-reader="true"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        // Click-through normally so clicks reach real elements. While PLACING a new
        // item, the layer captures the click to compute the drop point.
        pointerEvents: placing ? 'auto' : 'none',
        cursor: placing ? 'crosshair' : 'default',
        // While placing, the layer must sit ABOVE everything (overlay/fixed header,
        // sticky sections, elevated cards) so a click ANYWHERE — even over an existing
        // element or where items overlap — lands on the layer and drops the new item.
        zIndex: placing ? 2147483000 : 50,
        // Confine every overlay item's z-index to THIS layer's stacking context. Items
        // use very large z-indexes while editing (up to ~2.1e9 for selection/handles);
        // without isolation those leak into the root stacking context and paint OVER the
        // sticky/fixed header. `isolation: isolate` makes the layer a self-contained
        // context whose OWN z-index (50 when idle) sits below the header (9999), so the
        // header always overlays the content and sections scroll beneath it. When
        // placing, the layer's own z-index (2147483000) still sits above the header so
        // drop clicks land correctly.
        isolation: 'isolate',
        overflow: 'hidden',
      }}
      onClick={placing ? (e) => {
        if (typeof window !== 'undefined' && window.__AVPS_PLACING__ !== true) return;
        const el = layerRef.current;
        if (!el || typeof editing.onPlace !== 'function') return;
        const rect = el.getBoundingClientRect();
        const sf = scaleFactor > 0 ? scaleFactor : 1;
        const x = Math.max(0, Math.round((e.clientX - rect.left) / sf));
        const y = Math.max(0, Math.round((e.clientY - rect.top) / sf));
        const doc = el.ownerDocument || document;
        let sectionId = null;
        let relY = y;
        const stack = typeof doc.elementsFromPoint === 'function' ? doc.elementsFromPoint(e.clientX, e.clientY) : [];
        for (let i = 0; i < stack.length; i += 1) {
          const node = stack[i];
          if (!node || node === el) continue;
          const s = node.closest && node.closest('[data-reader-section-id]');
          if (s) {
            sectionId = s.getAttribute('data-reader-section-id');
            const sTopPx = s.getBoundingClientRect().top - rect.top;
            relY = Math.max(0, Math.round(y - sTopPx / sf));
            break;
          }
        }
        if (typeof window !== 'undefined') window.__AVPS_PLACING__ = false;
        editing.onPlace(x, relY, sectionId);
      } : undefined}
    >
      {topLevelItems.map((item, index) => renderOverlayItem(item, index, null))}
      {false && items.map((rawItem, index) => {
        if (!isPlainObject(rawItem)) return null;
        const geometryResult = validateElementLayout(rawItem.geometry);
        if (!geometryResult.ok) return null;

        const item = {
          id: rawItem.id,
          type: rawItem.type,
          geometry: geometryResult.value,
          content: isPlainObject(rawItem.content) ? rawItem.content : {},
          style: isPlainObject(rawItem.style) ? rawItem.style : {},
        };

        // Hidden items: render NOTHING on the published page. While editing they stay
        // visible (dimmed) so the author can select and un-hide them.
        if (item.style.hidden && !itemEditing) return null;

        // If the item is anchored to a real section, position it relative to THAT
        // section's current top (so it moves with the section on reorder/swap). The
        // stored geometry.y is section-relative; add the measured section pixel offset
        // (converted to reference units) before scaling. Fall back to absolute page
        // coordinates for legacy items with no anchor.
        const anchorId = typeof item.content.anchorSectionId === 'string' ? item.content.anchorSectionId : '';
        const sfForAnchor = scaleFactor > 0 ? scaleFactor : 1;
        const renderGeometry = anchorId && sectionOffsets[anchorId]
          ? { ...item.geometry, y: item.geometry.y + sectionOffsets[anchorId].top / sfForAnchor }
          : item.geometry;

        const box = scaleLayout(renderGeometry, scaleFactor);
        const resolved = resolveItemStyle(item, sectionLike, templateTheme);
        const st = item.style;

        const isPaper = item.type === 'paper';
        const isContainer = item.type === 'container';
        const radius = num(st.borderRadius, isPaper ? 12 : item.type === 'button' ? 8 : 0);
        const borderWidth = num(st.borderWidth, isContainer ? 1 : 0);
        const borderColor = st.borderColor || (isContainer ? '#cbd5e1' : '#e5e7eb');
        const padding = num(st.padding, isPaper || isContainer ? 16 : 0);
        const shadow = SHADOWS[st.boxShadow] || (isPaper ? SHADOWS.md : 'none');
        const opacity = num(st.opacity, 1);

        // Background follows the console's ItemView per-type rule. An IMAGE shows a WHITE
        // box until a picture is uploaded (so the dropped item is visible), then becomes
        // transparent once it has a src. Grid is always transparent (no fill behind cells).
        const backgroundColor = isPaper
          ? resolved.backgroundColor || '#ffffff'
          : isContainer
            ? resolved.backgroundColor || 'transparent'
            : item.type === 'text'
              ? resolved.backgroundColor
              : item.type === 'image'
                ? (item.content.src ? undefined : '#ffffff')
                : item.type === 'grid' || item.type === 'icon' || item.type === 'divider' || item.type === 'shape' || item.type === 'social' || item.type === 'quote' || item.type === 'video' || item.type === 'spacer' || item.type === 'card' || item.type === 'stat' || item.type === 'badge' || item.type === 'list' || item.type === 'progress' || item.type === 'rating' || item.type === 'accordion' || item.type === 'tabs' || item.type === 'map' || item.type === 'embed' || item.type === 'audio'
                  ? undefined
                  : resolved.backgroundColor;

        const baseBoxStyle = {
          position: 'absolute',
          top: 0,
          left: 0,
          transform: `translate(${box.x}px, ${box.y}px)${num(st.rotation, 0) ? ` rotate(${num(st.rotation, 0)}deg)` : ''}`,
          width: box.width,
          height: box.height,
          zIndex: box.z,
          boxSizing: 'border-box',
          backgroundColor,
          backgroundImage: backgroundImageLayer(st.backgroundImage),
          backgroundSize: backgroundImageLayer(st.backgroundImage) ? 'cover' : undefined,
          backgroundPosition: backgroundImageLayer(st.backgroundImage) ? 'center' : undefined,
          backgroundRepeat: backgroundImageLayer(st.backgroundImage) ? 'no-repeat' : undefined,
          opacity,
          borderRadius: radius || undefined,
          border: (borderWidth && item.type !== 'shape') ? `${borderWidth}px solid ${borderColor}` : undefined,
          boxShadow: shadow !== 'none' ? shadow : undefined,
          padding: (isPaper || isContainer) && padding ? padding : undefined,
          // Individual items re-enable pointer events over the transparent layer — but
          // NOT while placing, so a click over an existing item still falls through to
          // the layer and drops the new item there (drop anywhere, even overlapping).
          pointerEvents: placing ? 'none' : 'auto',
        };

        // In edit mode (not placing), float items ABOVE section content so a just-dropped
        // item is always clickable / draggable / resizable even when it overlaps a real
        // section element. The published page and placement mode keep the authored z.
        if (itemEditing) {
          baseBoxStyle.zIndex = 1000000 + (Number.isFinite(box.z) ? box.z : 0);
          if (selectedDescendants.has(item.id)) {
            baseBoxStyle.zIndex = 2147482001 + (Number.isFinite(box.z) ? box.z : 0);
          }
          // Make freshly dropped / empty items VISIBLE while editing so the author can see
          // and select them. An empty image (no src), an empty grid, or a transparent
          // container otherwise render with no pixels and look like "nothing dropped".
          const emptyImage = item.type === 'image' && !item.content.src;
          const emptyGrid = item.type === 'grid'
            && !(Array.isArray(item.content.cells) && item.content.cells.some((c) => c && c.src));
          const noFill = !backgroundColor || backgroundColor === 'transparent';
          const emptyBox = (item.type === 'container' || item.type === 'paper') && noFill && !borderWidth;
          if (emptyImage || emptyGrid || emptyBox) {
            baseBoxStyle.outline = '2px dashed #6366f1';
            baseBoxStyle.outlineOffset = '-2px';
            baseBoxStyle.backgroundColor = backgroundColor && backgroundColor !== 'transparent'
              ? backgroundColor
              : 'rgba(99,102,241,0.10)';
          }
          // Hidden items stay visible-but-dimmed while editing so they can be re-selected.
          if (item.style.hidden) {
            baseBoxStyle.opacity = 0.4;
            baseBoxStyle.outline = '2px dashed #94a3b8';
            baseBoxStyle.outlineOffset = '-2px';
          }
        }

        return (
          <OverlayItem
            key={item.id != null ? item.id : index}
            item={item}
            box={box}
            resolved={resolved}
            st={st}
            radius={radius}
            baseBoxStyle={baseBoxStyle}
            editing={itemEditing}
          />
        );
      })}
    </div>
  );
}
